import { useState } from "react";
import type { ReactNode } from "react";
import type { BlockStatus, RasterStatus } from "@/lib/types";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import { EASE_DATA } from "@/lib/utils";
import { useLanguage } from "@/i18n";
// shadcn primitives
import { Button as ShadcnButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

/* ────────────────────────────────────────────────────────────────────────────
   Status
   ────────────────────────────────────────────────────────────────────────────
   Every status is rendered as dot + text label, never colour alone. That isn't
   just good practice here, it's required: on this light surface the `warning`
   step measures 1.79:1 contrast, so a viewer with low vision or a colour vision
   deficiency cannot rely on the swatch. The label carries the meaning; the
   colour only speeds up scanning for everyone else.
──────────────────────────────────────────────────────────────────────────── */

type StatusTone = "good" | "warning" | "serious" | "critical" | "neutral";

const TONE_DOT: Record<StatusTone, string> = {
  good: "bg-status-good",
  warning: "bg-status-warning",
  serious: "bg-status-serious",
  critical: "bg-status-critical",
  neutral: "bg-status-neutral",
};

const TONE_TEXT: Record<StatusTone, string> = {
  good: "text-accent-deep font-semibold",
  warning: "text-ink-secondary",
  serious: "text-ink-secondary",
  critical: "text-status-text-critical",
  neutral: "text-ink-muted",
};

export function StatusBadge({
  tone,
  label,
  pulse = false,
  className,
}: {
  tone: StatusTone;
  label: string;
  pulse?: boolean;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-2 text-xs", className)}>
      <span className="relative flex h-2 w-2 flex-none">
        {pulse && (
          <span
            className={cn(
              "absolute inline-flex h-full w-full animate-ping rounded-full opacity-60",
              TONE_DOT[tone]
            )}
          />
        )}
        <span className={cn("relative inline-flex h-2 w-2 rounded-full", TONE_DOT[tone])} />
      </span>
      <span className={cn("whitespace-nowrap font-medium", TONE_TEXT[tone])}>{label}</span>
    </span>
  );
}

const RASTER_STATUS: Record<RasterStatus, { tone: StatusTone; label: string; pulse: boolean }> = {
  // Still streaming to the server's disk -- pulses, because unlike "Queued"
  // this is actively moving bytes and a multi-GB upload sits here for a while.
  receiving: { tone: "warning", label: "Uploading", pulse: true },
  uploaded: { tone: "neutral", label: "Queued", pulse: false },
  processing: { tone: "warning", label: "Converting", pulse: true },
  ready: { tone: "good", label: "Ready", pulse: false },
  failed: { tone: "critical", label: "Failed", pulse: false },
};

export function RasterStatusBadge({ status }: { status: RasterStatus }) {
  const { t } = useLanguage();
  const cfg = RASTER_STATUS[status] ?? { tone: "neutral" as StatusTone, label: status, pulse: false };
  return <StatusBadge tone={cfg.tone} label={cfg.label} pulse={cfg.pulse} />;
}

export const BLOCK_STATUS: Record<BlockStatus, { tone: StatusTone; label: string }> = {
  // "pending" is the absence of activity, so it takes the neutral step rather
  // than a status hue -- reserving the status palette for actual states keeps
  // "nothing has happened here" from competing visually with "needs attention".
  pending: { tone: "neutral", label: "Not started" },
  in_progress: { tone: "warning", label: "In progress" },
  submitted: { tone: "good", label: "Submitted" },
};

export function BlockStatusBadge({ status }: { status: BlockStatus }) {
  const { t } = useLanguage();
  const cfg = BLOCK_STATUS[status] ?? { tone: "neutral" as StatusTone, label: status };
  return <StatusBadge tone={cfg.tone} label={cfg.label} />;
}

/* ────────────────────────────────────────────────────────────────────────────
   Stat tile
   ────────────────────────────────────────────────────────────────────────────
   A single number needs no plot. These use proportional figures (the default)
   because they stand alone; `tabular-nums` is reserved for columns that have to
   align down a table.
──────────────────────────────────────────────────────────────────────────── */

export function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
  className,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: StatusTone | "accent";
  /** e.g. "self-stretch" to match the height of tiles beside it. */
  className?: string;
}) {
  const accentBar =
    tone === "accent"
      ? "bg-accent-solid"
      : tone === "good"
      ? "bg-status-good"
      : TONE_DOT[tone as StatusTone] ?? "bg-status-neutral";

  return (
    // self-start stops the tile stretching to match a taller neighbour in the
    // same grid row (the progress panel). Grid items default to `stretch`,
    // which left these as tall, mostly-empty boxes -- a number needs only the
    // space it occupies.
    <div className={cn("relative self-start overflow-hidden rounded-md border border-line bg-surface px-4 py-3", className)}>
      {/* A 2px rule rather than a tinted card: it identifies the metric without
          washing a colour across a whole panel. */}
      <span className={cn("absolute left-0 top-0 h-full w-[2px]", accentBar)} aria-hidden="true" />
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold leading-none tabular-nums text-ink">{value}</p>
      {hint && <p className="mt-1 text-2xs text-ink-muted">{hint}</p>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Status distribution bar
   ────────────────────────────────────────────────────────────────────────────
   One stacked bar + a legend carrying the counts. Chosen over a pie or donut
   because the question is "how much is left", which is a length comparison; and
   over three separate bars because the parts genuinely sum to a meaningful
   whole (every block is in exactly one state).

   The 2px surface-coloured gaps between segments are load-bearing, not
   decorative: they keep adjacent fills from reading as one mass, which is the
   secondary encoding that makes the neutral/warning pair separable.
──────────────────────────────────────────────────────────────────────────── */

export type DistributionSegment = {
  key: string;
  label: string;
  count: number;
  tone: StatusTone;
};

export function StatusDistribution({
  segments,
  total,
  caption,
}: {
  segments: DistributionSegment[];
  total: number;
  caption?: string;
}) {
  const present = segments.filter((s) => s.count > 0);
  const { t } = useLanguage();

  return (
    <div className="rounded-md border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">
          {t.ui.surveyProgress}
        </p>
        {caption && <p className="text-xs text-ink-muted">{caption}</p>}
      </div>

      {/* The empty case renders the same structure as the populated one -- an
          empty bar track plus a legend of zeros -- rather than swapping the
          whole body for a "No blocks defined yet." line.

          That swap made this panel 36px shorter when total was 0, and on the
          projects page the summary follows whichever row the pointer is over.
          Passing the cursor between an empty project and a populated one
          therefore resized this panel and shoved the entire project table up
          and down by 36px on every crossing, which is what "the progress bar
          flickers on hover" actually was. Nothing is lost by dropping the
          message: a legend reading 0 / 0 / 0 next to a "Blocks 0" tile already
          says there is nothing defined yet. */}
      <div className="mt-3 flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full bg-surface-sunken">
        {total > 0 &&
          present.map((s) => (
            <motion.span
              key={s.key}
              className={cn("h-full first:rounded-l-full last:rounded-r-full", TONE_DOT[s.tone])}
              initial={{ width: 0 }}
              animate={{ width: `${(s.count / total) * 100}%` }}
              transition={EASE_DATA}
              aria-hidden="true"
            />
          ))}
      </div>

      {/* Legend with counts. This is the "relief" the contrast check
          requires -- the numbers are never colour-only. */}
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-2">
            <span className={cn("h-2 w-2 flex-none rounded-full", TONE_DOT[s.tone])} />
            <span className="text-xs text-ink-secondary">{s.label}</span>
            <span className="tabular text-xs font-semibold text-ink">{s.count}</span>
            <span className="text-2xs text-ink-muted">
              {total > 0 ? `${Math.round((s.count / total) * 100)}%` : "—"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Layout / feedback primitives
──────────────────────────────────────────────────────────────────────────── */

export function Panel({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  /** Text is shown as the heading; anything else (e.g. tabs) as given. */
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("flex min-h-0 flex-col rounded-md border border-line bg-surface", className)}>
      {(title || actions) && (
        <header className="flex flex-none items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          {typeof title === "string" ? (
            title && <h2 className="font-display text-[15px] font-semibold uppercase tracking-[0.05em] text-ink">{title}</h2>
          ) : (
            title
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn("min-h-0 flex-1", bodyClassName)}>{children}</div>
    </section>
  );
}

/**
 * Button — thin wrapper around shadcn Button keeping the portal's existing
 * prop API (variant: primary|secondary|ghost|danger, size: sm|md) so all
 * call-sites compile without changes.
 */
export function Button({
  variant = "secondary",
  size = "md",
  className,
  children,
  ...props
}: {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  // Map portal size names to shadcn size names
  const shadcnSize = size === "sm" ? "sm" : "default";
  return (
    <ShadcnButton
      variant={variant as "primary" | "secondary" | "ghost" | "danger"}
      size={shadcnSize}
      className={className}
      {...props}
    >
      {children}
    </ShadcnButton>
  );
}

/** An on/off switch. `label` is read out to screen readers. */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={cn(
        "relative inline-flex h-4 w-7 flex-none items-center rounded-full transition disabled:opacity-40",
        checked ? "bg-accent-solid" : "bg-line-strong",
        className
      )}
    >
      <span
        className={cn(
          "inline-block h-3 w-3 rounded-full bg-white shadow transition",
          checked ? "translate-x-3.5" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

export function Banner({
  tone,
  children,
  action,
}: {
  tone: "info" | "success" | "warning" | "danger";
  children: ReactNode;
  action?: ReactNode;
}) {
  const styles = {
    info: "bg-accent-subtle text-accent-text",
    success: "bg-accent-subtle text-accent-deep",
    warning: "bg-status-warning/12 text-ink",
    danger: "bg-status-critical/10 text-ink",
  }[tone];
  const rule = {
    info: "bg-accent",
    success: "bg-status-good",
    warning: "bg-status-warning",
    danger: "bg-status-critical",
  }[tone];

  return (
    <div className={cn("relative flex items-start gap-3 overflow-hidden rounded px-4 py-2.5", styles)}>
      <span className={cn("absolute left-0 top-0 h-full w-[2px]", rule)} aria-hidden="true" />
      <p className="flex-1 text-xs leading-relaxed">{children}</p>
      {action}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <p className="text-sm font-semibold text-ink-secondary">{title}</p>
      <p className="mt-1 max-w-sm text-xs leading-relaxed text-ink-muted">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  const { t } = useLanguage();
  return (
    <span
      className={cn(
        "inline-block animate-spin rounded-full border-2 border-line border-t-signal",
        className ?? "h-4 w-4"
      )}
      role="status"
      aria-label={t.common.loading}
    />
  );
}

/**
 * Password field with a show/hide eye toggle.
 *
 * One component rather than the toggle repeated per form: the portal has three
 * password inputs (sign-in, create-account, reset-password) and only sign-in
 * had a reveal at all -- and that one was a text "Show"/"Hide" button, so the
 * three did not look like the same control. These credentials are typed by an
 * administrator reading a password aloud to a surveyor or off a note, so being
 * able to check what was typed matters more here than in a consumer login.
 *
 * `type` is swapped rather than using -webkit-text-security so the revealed
 * value is real text: a password manager, and Ctrl+A/copy, both behave.
 */
export function PasswordInput({
  value,
  onChange,
  autoComplete,
  placeholder,
  className,
  autoFocus,
  disabled,
  id,
}: {
  value: string;
  onChange: (next: string) => void;
  autoComplete?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
}) {
  const [shown, setShown] = useState(false);
  const { t } = useLanguage();

  return (
    <div className="relative">
      <input
        id={id}
        type={shown ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        // pr-9 leaves room for the button so a long value never slides under it.
        className={
          className ??
          "h-9 w-full rounded border-0 bg-surface-sunken pl-2.5 pr-9 text-sm text-ink shadow-ring focus:bg-surface-raised disabled:opacity-50"
        }
      />
      <button
        type="button"
        // Not disabled with the field: revealing what was already typed is
        // still useful while a submit is in flight, and it changes nothing.
        onClick={() => setShown((v) => !v)}
        // The button carries the label; aria-pressed conveys the state. Without
        // an accessible name this is an unlabelled icon button to a screen
        // reader, since the only content is a decorative SVG.
        aria-label={shown ? t.ui.hidePassword : t.ui.showPassword}
        aria-pressed={shown}
        title={shown ? t.ui.hidePassword : t.ui.showPassword}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-ink-muted transition hover:bg-surface-raised hover:text-ink-secondary"
      >
        <EyeIcon off={shown} />
      </button>
    </div>
  );
}

/**
 * Eye / eye-with-slash, inline rather than from an icon package.
 *
 * This portal ships no icon library (nothing else in ui.tsx draws an SVG), and
 * adding a dependency for two paths would be the larger change.
 *
 * The slash shows when the password is VISIBLE -- the icon depicts the action
 * the button will take ("hide"), which is the convention every browser and
 * password manager uses. Showing an open eye while the text is already visible
 * is the common inversion of this and reads as broken.
 */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <path d="M3 3l18 18" />}
    </svg>
  );
}

/**
 * Modal confirmation for a destructive action.
 *
 * Replaces four different patterns that had grown up side by side: an inline
 * expanding row (accounts), `window.confirm` (project, raster, reference
 * layer), two chained `window.confirm`s (hierarchy clear), and -- for deleting
 * a surveyor's collected shape, the most valuable data in the system -- nothing
 * at all. The inconsistency was the bug: a re-uploadable GeoJSON overlay asked
 * twice while a field shape someone walked to and drew went in one click.
 *
 * A real dialog rather than `window.confirm` because these prompts have to
 * carry structure -- counts of what will be lost, and in the hierarchy case an
 * Export button that must be used before Delete unlocks. A native confirm can
 * only show a string and offer OK/Cancel.
 *
 * `children` is the slot for that extra content; `confirmDisabled` is what lets
 * a caller gate the action on something happening first.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  danger = true,
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  /** The consequence, in plain words. Say what SURVIVES as well as what goes. */
  body: ReactNode;
  /** Restates the verb and noun ("Delete account"), never "Yes" or "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  const { t } = useLanguage();
  return (
    // Radix Dialog handles: focus trap, Esc key, backdrop dismiss, aria-modal,
    // and focus restoration — removing ~25 lines of manual useRef/useEffect.
    // onOpenChange fires on both backdrop-click and Esc; we gate on !busy so an
    // in-flight request is not orphaned by a mis-click.
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-xs leading-relaxed text-ink-secondary">{body}</div>
          </DialogDescription>
        </DialogHeader>

        {children && <div className="mt-3">{children}</div>}

        <DialogFooter>
          <Button size="sm" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            size="sm"
            variant={danger ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? <Spinner className="h-3 w-3" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The company mark.
 *
 * Renders `public/logo.png` when that file exists, and falls back to the
 * lettermark otherwise — so the portal never shows a broken-image icon in its
 * own header, and dropping the real logo in requires no code change.
 *
 * `public/` is served at the site root by Vite in dev and copied verbatim into
 * `dist/` on build, so the same `/logo.png` path works in both without an
 * import or a bundler asset hash.
 *
 * The image is decorative here: every place it appears sits directly beside the
 * word "Sthapatya", so an alt text would be read out twice by a screen reader.
 * Hence alt="" rather than a description.
 */
export function BrandMark({ className }: { className?: string }) {
  const [failed, setFailed] = useState(false);
  const box = className ?? "h-6 w-6";

  if (failed) {
    return (
      <span
        className={cn(
          "flex flex-none items-center justify-center rounded bg-accent font-semibold text-white",
          box
        )}
        // Sized off the box so the letter stays proportional at either size the
        // portal uses (h-6 in the sidebar, h-8 on the sign-in page).
        style={{ fontSize: "0.65em" }}
        aria-hidden="true"
      >
        S
      </span>
    );
  }

  return (
    <img
      src="/logo.png"
      alt=""
      aria-hidden="true"
      onError={() => setFailed(true)}
      // object-contain, not cover: a logo must not be cropped to fill a square.
      className={cn("flex-none rounded object-contain", box)}
    />
  );
}
